import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import { useFavorites } from '@/hooks/useFavorites';
import { useHistory } from '@/hooks/useHistory';

function formatTime(timestamp: number) {
  const date = new Date(timestamp);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function FavoritesPage() {
  const { favorites, removeFavorite, clearFavorites } = useFavorites();
  const { history, removeHistory, clearHistory } = useHistory();
  const [tab, setTab] = useState<'favorites' | 'history'>('favorites');
  const [toast, setToast] = useState('');

  const favoriteList = useMemo(
    () =>
      favorites
        .map((item) => ({ ...item, comic: comics.find((comic) => comic.id === item.comicId) }))
        .filter((item) => item.comic),
    [favorites],
  );

  const historyList = useMemo(
    () =>
      history
        .map((item) => ({ ...item, comic: comics.find((comic) => comic.id === item.comicId) }))
        .filter((item) => item.comic),
    [history],
  );

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2000);
  };

  const isFav = tab === 'favorites';
  const list = isFav ? favoriteList : historyList;

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8">
      <nav className="mb-4 flex items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="transition hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">我的追漫</span>
      </nav>

      <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">收藏与浏览历史</h1>
          <p className="mt-2 text-sm text-foreground-600">
            收藏与历史保存在你的浏览器本地，清除浏览器数据后将会丢失。
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-full bg-background-100 p-1">
          <button
            type="button"
            onClick={() => setTab('favorites')}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition cursor-pointer ${
              isFav ? 'bg-primary-500 font-medium text-background-50' : 'text-foreground-700'
            }`}
          >
            我的收藏 {favoriteList.length > 0 ? `(${favoriteList.length})` : ''}
          </button>
          <button
            type="button"
            onClick={() => setTab('history')}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition cursor-pointer ${
              !isFav ? 'bg-primary-500 font-medium text-background-50' : 'text-foreground-700'
            }`}
          >
            浏览历史 {historyList.length > 0 ? `(${historyList.length})` : ''}
          </button>
        </div>
      </header>

      {list.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
          <span className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className={isFav ? 'ri-heart-3-line' : 'ri-history-line'} />
          </span>
          <p className="text-sm text-foreground-700">
            {isFav ? '还没有收藏任何漫画' : '还没有浏览记录'}
          </p>
          <p className="mt-1 text-xs text-foreground-500">
            {isFav ? '在漫画详情页点击「收藏」，作品就会出现在这里' : '阅读过的章节会自动记录在这里'}
          </p>
          <Link
            to="/list"
            className="mt-5 flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition hover:bg-primary-600"
          >
            <i className="ri-compass-3-line" />
            去发现好漫
          </Link>
        </div>
      ) : (
        <>
          {isFav && list.length > 0 && (
            <div className="mb-4 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  clearFavorites();
                  showToast('已清空全部收藏');
                }}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-xs text-foreground-700 transition hover:border-primary-400 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-delete-bin-6-line" />
                清空全部
              </button>
            </div>
          )}
          {!isFav && list.length > 0 && (
            <div className="mb-4 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  clearHistory();
                  showToast('已清空浏览历史');
                }}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-xs text-foreground-700 transition hover:border-primary-400 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-delete-bin-6-line" />
                清空历史
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((item) => {
              const comic = item.comic!;
              return (
                <div
                  key={comic.id}
                  className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition hover:border-primary-300"
                >
                  <Link to={`/detail/${comic.id}`} className="h-28 w-20 shrink-0 overflow-hidden rounded bg-background-200">
                    <img src={comic.cover} alt={comic.title} className="h-full w-full object-cover object-top" />
                  </Link>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <Link to={`/detail/${comic.id}`} className="truncate text-sm font-medium text-foreground-900 transition hover:text-primary-600">
                      {comic.title}
                    </Link>
                    <p className="mt-0.5 truncate text-xs text-foreground-500">{comic.author}</p>
                    <p className="mt-1 truncate text-xs text-foreground-500">
                      {isFav ? `收藏于 ${formatTime(item.addedAt)}` : item.chapterTitle}
                    </p>
                    <div className="mt-auto flex items-center gap-2 pt-2">
                      <Link
                        to={`/read/${comic.id}/${isFav ? comic.chapters[0].id : item.chapterId}`}
                        className="flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-3 py-1.5 text-xs font-medium text-background-50 transition hover:bg-primary-600"
                      >
                        <i className="ri-book-open-line" />
                        {isFav ? '开始阅读' : '继续阅读'}
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          if (isFav) {
                            removeFavorite(comic.id);
                            showToast('已移出收藏');
                          } else {
                            removeHistory(comic.id);
                            showToast('已删除该历史');
                          }
                        }}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-background-300 text-foreground-600 transition hover:border-primary-400 hover:text-primary-600 cursor-pointer"
                        aria-label="移除"
                      >
                        <i className="ri-close-line" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 animate-fadeUp rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}